import React from "react";
import { RecBar, RecIcon } from "../materials/MaterialRecordParts";
import { BarMenu } from "../timelogs/TimeLogBarMenu";
import { formatTaskDate, taskPathLabel, TASK_STATUS } from "../../helpers/tasks/taskModel";
import { formatLogTimestamp } from "../timelogs/timeLogModel";
import { formatDuration, progressValue } from "../../helpers/tasks/taskDuration";
import { expectedCompletionForTask, formatScheduleDate, SCHEDULE_STATUS } from "../../helpers/tasks/workSchedule";
import { useWorkSchedule } from "../../hooks/useWorkSchedule";
import { DueChip, PriorityChip, ProgressBar, StatusChip, TaskCheck } from "./TaskParts";
import "./tasks.css";

function Info({ icon, label, children, onOpen, actionLabel, hint = "", testId }) {
  const body = (
    <>
      <span className="tl-info__icon">
        <RecIcon name={icon} />
      </span>
      <span className="tl-info__body">
        <span className="tl-info__label">{label}</span>
        <span className="tl-info__value">{children}</span>
        {hint ? <span className="tk-info-hint">{hint}</span> : null}
      </span>
      {onOpen ? <RecIcon name="chevron" className="tl-info__chev" /> : null}
    </>
  );
  if (!onOpen) {
    return (
      <div className="tl-info" title={hint || undefined} data-testid={testId}>
        {body}
      </div>
    );
  }
  return (
    <button type="button" className="tl-info tl-info--action" onClick={onOpen} aria-label={actionLabel}>
      {body}
    </button>
  );
}

/**
 * Expected Completion row text, or "" when the task has neither a start nor a
 * duration (legacy due-only tasks show no extra row). Never invented.
 */
export function expectedCompletionText(task, workSchedule) {
  if (!task || (!task.start_date && !task.planned_duration_minutes)) return "";
  if (!task.start_date) return "Add a start date to forecast";
  if (!task.planned_duration_minutes) return "Add a duration to forecast";
  if (!workSchedule || workSchedule.status === SCHEDULE_STATUS.UNKNOWN) return "Unavailable (work schedule not loaded)";
  const iso = expectedCompletionForTask(task, workSchedule.schedule);
  if (!iso) return "Unavailable";
  return formatScheduleDate(iso, { withYear: iso.slice(0, 4) !== String(new Date().getFullYear()) });
}

/** Information-first view of one Project Task. */
export function TaskDetails({
  task,
  projectName,
  locationName,
  featureName = "",
  assigneeName,
  projectId = "",
  canEdit,
  canComplete,
  busy,
  error,
  onBack,
  backLabel = "",
  onOpenProject,
  onEdit,
  onToggle,
  onDelete,
}) {
  const done = task.status === TASK_STATUS.COMPLETED;
  const projectDestinationId = task.project_id || projectId || "";
  const canOpenProject = Boolean(projectDestinationId && onOpenProject);
  // ⋮ opens the canonical editor (Edit Task). No new destructive actions.
  const menu = canEdit
    ? [
        { key: "edit", label: "Edit Task", onSelect: onEdit },
        { key: "delete", label: "Delete task", danger: true, onSelect: onDelete },
      ]
    : [];
  const progress = progressValue(task);
  const workSchedule = useWorkSchedule();
  const duration = formatDuration(task.planned_duration_minutes, workSchedule.minutesPerDay);
  const expectedText = expectedCompletionText(task, workSchedule);

  return (
    <div className="screen nx-screen rec-page rec-page--detail" data-testid="task-details">
      <RecBar
        title="Task Details"
        onBack={onBack}
        backLabel={backLabel}
        trailing={
          <span className="tk-barmenu" data-testid="task-details-menu">
            <BarMenu items={menu} />
          </span>
        }
      />

      <div className="rec-body">
        {error ? (
          <div className="rec-banner" role="alert">
            {error}
          </div>
        ) : null}

        <section className="rec-card tk-hero">
          <TaskCheck task={task} interactive={false} />
          <h2 className={`tk-hero__title${done ? " is-done" : ""}`}>{task.title}</h2>
        </section>
        {taskPathLabel({ projectName, locationName, featureName }) ? (
          <div className="tk-path" data-testid="task-path">
            {taskPathLabel({ projectName, locationName, featureName })}
          </div>
        ) : null}
        <div className="tk-chips">
          <StatusChip status={task.status} />
          <PriorityChip priority={task.priority} />
          <DueChip task={task} />
        </div>

        {task.description ? (
          <section className="rec-card rec-desc">
            <div className="rec-label">
              <RecIcon name="doc" />
              Description
            </div>
            <p className="rec-desc__text">{task.description}</p>
          </section>
        ) : null}

        <section className="rec-card tl-infolist" aria-label="Details">
          <Info
            icon="folder"
            label="Project"
            onOpen={canOpenProject ? () => onOpenProject(projectDestinationId) : null}
            actionLabel={projectName ? `View tasks for ${projectName}` : "View project tasks"}
          >
            {projectName || (canOpenProject ? "View project tasks" : "—")}
          </Info>
          <Info icon="pin" label="Location">
            {locationName || "Whole project"}
          </Info>
          {featureName ? (
            <Info icon="tag" label="Feature">
              {featureName}
            </Info>
          ) : null}
          <Info icon="user" label="Assignee">
            {assigneeName || "Unassigned"}
          </Info>
          {task.start_date ? (
            <Info icon="calendar" label="Start" testId="task-detail-start">
              {formatTaskDate(task.start_date, { year: true })}
            </Info>
          ) : null}
          {duration ? (
            <Info icon="clock" label="Planned duration" hint="Estimated working time" testId="task-detail-duration">
              {duration}
            </Info>
          ) : null}
          {expectedText ? (
            <Info
              icon="calendar"
              label="Expected completion"
              hint="Forecast from start + duration + work schedule"
              testId="task-detail-expected"
            >
              {expectedText}
            </Info>
          ) : null}
          {task.due_date ? (
            <Info icon="calendar" label="Due" hint="Deadline" testId="task-detail-due">
              {formatTaskDate(task.due_date, { year: true })}
            </Info>
          ) : null}
          {done && task.completed_at ? (
            <Info icon="check" label="Actual completion" testId="task-detail-actual">
              {formatLogTimestamp(task.completed_at)}
            </Info>
          ) : null}
          <Info icon="chart" label="Progress">
            {progress == null ? (
              "Not reported"
            ) : (
              <span className="tk-progress-row" data-testid="task-progress">
                <ProgressBar value={progress} />
                <span>{progress}%</span>
              </span>
            )}
          </Info>
        </section>

        <div className="tl-meta">
          {task.created_at ? <div>Created {formatLogTimestamp(task.created_at)}</div> : null}
          {task.updated_at ? <div>Updated {formatLogTimestamp(task.updated_at)}</div> : null}
        </div>
        <div className="pj-pagepad" aria-hidden="true" />
      </div>

      {canComplete ? (
        <div className="rec-actions">
          <button
            type="button"
            className={`rec-btn ${done ? "rec-btn--outline" : "rec-btn--primary"}`}
            disabled={busy}
            onClick={onToggle}
          >
            <RecIcon name="check" />
            {done ? "Reopen" : "Mark Complete"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
